"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-3 w-16 bg-muted animate-pulse" />)}
      </div>;
  }
  return <ul className="flex items-center justify-center gap-8">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group relative inline-block text-foreground text-xs font-medium uppercase tracking-[0.14em] transition-colors duration-200 hover:text-primary">
            <span>{item.label}</span>
            <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-200 ease-out group-hover:w-6" />
          </Link>
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-40 bg-muted animate-pulse" />)}
      </div>;
  }
  return <ul className="flex flex-col gap-6">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onNavigate} className="font-default text-3xl font-medium tracking-tight text-foreground hover:text-primary transition-colors duration-200">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="19496" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-200 ease-out bg-background text-foreground ${scrolled ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-background"}`}>
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-12">
        <div className="grid h-16 grid-cols-12 items-center md:h-20">
          <div className="col-span-6 md:col-span-3 flex items-center">
            <Link to="/" className="inline-flex items-center" aria-label="Inclusive Voices News home">
              <WvcLogo className="h-5 w-auto" />
            </Link>
          </div>

          <nav className="hidden md:flex md:col-span-6 items-center justify-center">
            <MenuProvider menu_id="31">
              <DesktopMenuConsumer />
            </MenuProvider>
          </nav>

          <div className="col-span-6 md:col-span-3 flex items-center justify-end">
            <div className="hidden md:block">
              <Button asChild variant="outline" className="group relative h-10 border border-foreground bg-transparent px-6 text-xs font-medium uppercase tracking-[0.14em] text-foreground hover:bg-primary hover:text-background transition-colors duration-200">
                <Link to="/ConnectPage">Join the Conversation</Link>
              </Button>
            </div>

            <div className="md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" className="h-auto px-2 py-1 text-xs font-medium uppercase tracking-[0.14em] text-foreground hover:bg-transparent hover:text-primary" aria-label="Open menu">
                    <Menu className="size-4" />
                    <span>Menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-full border-l-0 bg-background p-0">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <div className="flex h-full flex-col">
                    <div className="flex items-center justify-between px-5 h-16 border-b border-border">
                      <SheetClose asChild>
                        <Link to="/" className="inline-flex items-center" aria-label="Inclusive Voices News home">
                          <WvcLogo className="h-5 w-auto" />
                        </Link>
                      </SheetClose>
                      <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground pr-10">
                        Menu
                      </span>
                    </div>

                    <div className="flex-1 px-5 pt-16">
                      <div className="ml-[8.333%]">
                        <MenuProvider menu_id="31">
                          <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                        </MenuProvider>
                      </div>
                    </div>

                    <div className="px-5 pb-10 ml-[8.333%]">
                      <SheetClose asChild>
                        <Button asChild variant="outline" className="h-12 border border-foreground bg-transparent px-7 text-xs font-medium uppercase tracking-[0.14em] text-foreground hover:bg-primary hover:text-background transition-colors duration-200">
                          <Link to="/ConnectPage">Join the Conversation</Link>
                        </Button>
                      </SheetClose>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}